<script>
import i18n from '@/i18n';
import {mapActions, mapState} from "pinia";
import {Source} from "@/store/model/Source";
import {proposalStore} from "@/store/proposalStore";
import {sourceReferencesStore} from "@/store/sourceReferencesStore";
import {baseLoaderStore} from "@/store/baseLoaderStore";
import Dialog from "primevue/dialog";
import VuePdfApp from "vue3-pdf-app";
import SourceListItem from "@/components/Source/SourceListItem.vue";
import "vue3-pdf-app/dist/icons/main.css";

export default {
  name: "ActualProposalModal",
  props: {
    actualProposal: {
      type: Object
    }
  },
  components: {
    Dialog,
    VuePdfApp,
    SourceListItem
  },
  data() {
    return {
      modalTabs: {},
      sidebarState: false,
      sourceReferencesPopoverState: false,
      sourceReferencesData: null,
      config: {
        sidebar: false,
        secondaryToolbar: false,
        toolbar: false,
        errorWrapper: true,
      }
    }
  },
  computed: {
    ...mapState(proposalStore, [
        'actualProposalModalState',
        'actualProposalContent'
    ]),
    modalState: {
      get() {
        return this.actualProposalModalState;
      },
      set() {
        this.closeActualProposalModal();
      },
    },
    actualProposalContentType() {
      return this.actualProposal.type;
    },
    sourceReferencesCount() {
      return this.sourceReferencesData.length;
    },
    sidebarToggleControlText() {
      return this.sidebarState
          ? i18n.global.t('actualProposal.hideListSourceReferences')
          : i18n.global.t('actualProposal.seeListSourceReferences')
    }
  },
  methods: {
    ...mapActions(baseLoaderStore, [
      'showBaseLoader',
      'hideBaseLoader'
    ]),
    ...mapActions(proposalStore, [
        'closeActualProposalModal',
        'getActualTextContent',
        'getActualDocContent'
    ]),
    ...mapActions(sourceReferencesStore,[
        'getSourceReferencesList'
    ]),
    documentLoaded() {
      this.hideBaseLoader();
    },
    toggleToggleSidebarState() {
      this.sidebarState = !this.sidebarState;
    },
  },
  mounted() {
    if (this.actualProposalContentType === "HTML_TEXT") {
      this.getActualTextContent(this.actualProposal.contentId).then(()=> {
        this.documentLoaded();
      });
    }

    this.getSourceReferencesList({
      page: 0,
      size: 10,
      targetContentIds: this.actualProposal.contentId
    }).then((resp)=> {
      this.sourceReferencesData = resp.data.content.map((i)=> {
        return {
          createdTime: i.createdTime,
          creator: i.creator,
          id: i.id,
          source: {
            source: new Source(i.source.source),
            sourceTitle: i.source.sourceTitle,
            sourceUrl: i.source.sourceUrl,
            type: i.source.type,
          },
          targets: i.targets,
          updatedTime: i.updatedTime
        }
      });
    });

    this.showBaseLoader();
  },
}
</script>

<template>
  <Dialog v-model:visible="modalState"
          class="actual-proposal-modal"
          modal
          maximizable
          :header="$t('actualProposal.title')"
  >
    <div class="actual-proposal-controls-wrap">
      <div class="actual-proposal-controls" >
        <div class="actual-proposal-control" @click="toggleToggleSidebarState">
          {{ sidebarToggleControlText }}
        </div>
      </div>
    </div>
    <div class="actual-proposal-main">
      <div class="actual-proposal-content">
        <template v-if="actualProposalContentType === 'HTML_TEXT'">
          <div v-html="actualProposalContent"
               class="actual-proposal-text"
          ></div>
        </template>
        <template v-else-if="actualProposalContentType === 'PDF'">
          <vue-pdf-app style="height: 650px;"
                       :pdf="`/api/attachment/${this.actualProposal.attachments[0].hash}/content`"
                       :config="config"
                       @pages-rendered="documentLoaded"
          ></vue-pdf-app>
        </template>
      </div>
      <div v-if="sidebarState" class="actual-proposal-sidebar">
        <span class="actual-proposal-sidebar__title">
          {{$t('actualProposal.allSourceReferences')}} ({{sourceReferencesCount}})
        </span>
        <div class="sources-list">
          <source-list-item v-for="item in sourceReferencesData"
                            :key="item.id"
                            :item-data="item"
          />
          <div v-for="item in sourceReferencesData" class="source-item"
               :key="item.id"
          >
          </div>
        </div>
      </div>
    </div>
    <template #footer>
      <button type="button" class="p-dialog-close-btn" @click="closeActualProposalModal()">
        {{$t('common.buttons.close')}}
      </button>
    </template>
  </Dialog>
</template>

<style lang="scss">
.actual-proposal-modal {
  min-width: 950px;
  max-width: 950px;
  min-height: 800px;

  .p-dialog-header-actions {
    .p-dialog-maximize-button {
      color: #7835a6;
      &:hover {
        background: transparent !important;
        color: #7835a6 !important;
      }
    }
  }

  .p-dialog-content {
    height: 710px;
    padding: 0;

    .actual-proposal-controls-wrap {
      width: 100%;
      margin-bottom: 10px;
      padding: 10px 15px;
      position: relative;
    }

    .actual-proposal-controls {
      display: flex;
      align-items: center;
      height: 30px;
      width: 100%;
      position: relative;
      padding: 0 15px;
      border-radius: 4px;
      box-shadow: 0 0 6px 0 rgba(79, 79, 79, 0.27);
      border: transparent;

      .actual-proposal-control {
        font-size: 14px;
        font-weight: 600;
        color: #4f4f4f;
        background: #fff;
        cursor: pointer;
        &:hover {
          color: #7835a6;
        }
        &--active {
          color: #7835a6;
          background: #fff;
        }
      }
    }

    .actual-proposal-main {
      display: flex;
      height: calc(100% - 60px);
    }

    .actual-proposal-content {
      width: 100%;

      .pdf-app {
        height: 100% !important;
      }
      .actual-proposal-text {
        max-width: 905px;
        padding: 0 15px 15px;
        font-size: 14px;
        line-height: 21px;
        color: #4f4f4f;

        * {
          margin: 0 !important;
          padding: 0 !important;
        }
      }

      .toolbar {
        display: none;
      }
    }

    .actual-proposal-sidebar {
      width: 260px;
      min-width: 260px;
      border-left: 1px solid #ccc;

      &__title {
        display: inline-block;
        width: 100%;
        margin-bottom: 10px;
        font-size: 14px;
        font-weight: 600;
        text-align: center;
        color: #4f4f4f;
      }
      .sources-list {
        overflow: hidden;
      }
    }

    #viewerContainer {
      position: absolute !important;
      left: 0 !important;
      background: #FFFFFF;

      .textLayer {
        max-width: 820px !important;
      }

      .page {
        width: 90% !important;
        max-width: 840px;
        height: 100% !important;
        margin-bottom: 10px;
        overflow: hidden;
        overflow-x: auto;

        canvas {
          height: 1125px !important;
          max-width: 820px !important;
        }
      }
    }

    .canvasWrapper {
      height: 100% !important;
      width: 90% !important;
      overflow: visible !important;
    }
  }

  &.p-dialog-maximized {
    max-width: 100%;
    .actual-proposal-text {
      max-width: 100% !important;
    }
  }
}

.source-info-popover {
  width: 380px;
  top: 25px !important;
  left: 0 !important;
  box-shadow: 0 0 6px 0 rgba(79, 79, 79, 0.27) !important;
  border-radius: 5px !important;
  .p-popover-content {
    padding: 20px 10px;
  }
}

</style>